/**
 * dietaryConflict — shared logic for "does this menu item / recipe
 * clash with the diner's saved allergen/dietary profile?"
 *
 * Usage:
 *   const conflicts = checkConflicts(item, profile);
 *   conflicts.hasAllergen   → ⚠ HARD BLOCK (red overlay)
 *   conflicts.missingDiet   → preference miss (greyed but orderable)
 *   conflicts.reasons       → array of human strings for tooltips
 */

// Map of recipe/menu dietary_tag CODE → list of allergen CODEs it
// directly signals as PRESENT. (Mirrors backend ALLERGEN_TO_DIETARY.)
const TAG_IMPLIES_ALLERGEN = {
  nuts:    ["nuts"],            // POSITIVE 'nuts' allergen flag
};

// And the inverse — "absence of this dietary tag means a risk for
// the corresponding allergen". e.g. an item that does NOT carry
// 'gluten_free' may contain gluten.
const ABSENCE_IMPLIES_ALLERGEN = {
  gluten:    "gluten_free",
  dairy:     "dairy_free",
};

// Likewise for ingredient-text scanning — keep in sync with
// backend hp_michelin _NUT_HINTS / _DAIRY_HINTS etc.
const INGREDIENT_ALLERGEN_HINTS = {
  nuts:      [/\balmond/i, /\bhazelnut/i, /\bwalnut/i, /\bpecan/i,
                /\bpistachio/i, /\bcashew/i, /\bpine ?nut/i,
                /\bmacadamia/i, /\bpraline/i, /\bfrangipane/i,
                /\bmarzipan/i, /\bnoisette/i],
  peanuts:   [/\bpeanut/i, /\bgroundnut/i],
  dairy:     [/\bbutter/i, /\bcream/i, /\bmilk/i, /\bcheese/i,
                /\byog(h)?urt/i, /\bghee/i, /\bcr[eè]me/i,
                /\bparmesan/i, /\bricotta/i, /\bmascarpone/i,
                /\bmozzarella/i],
  eggs:      [/\beggs?/i, /\boeuf/i, /\bquail egg/i],
  soy:       [/\bsoy/i, /\bsoya/i, /\btofu/i, /\bedamame/i,
                /\btempeh/i, /\bmiso/i, /\btamari/i],
  gluten:    [/\bflour/i, /\bwheat/i, /\bbread/i, /\bpasta/i,
                /\bnoodle/i, /\bpanko/i, /\bcouscous/i,
                /\bsemolina/i, /\bbarley/i, /\brye/i, /\bspelt/i,
                /\bpastry/i, /\bfilo/i, /\bphyll?o/i,
                /\bsoy sauce/i],
  shellfish: [/\bshrimp/i, /\bprawn/i, /\blobster/i, /\bcrab/i,
                /\boyster/i, /\bmussel/i, /\bscallop/i, /\bclam/i,
                /\bsquid/i, /\boctopus/i, /\bcalamari/i,
                /\bcrayfish/i],
  fish:      [/\btuna\b/i, /\bsalmon\b/i, /\bcod\b/i, /\banchovy/i,
                /\bsardine/i, /\btrout/i, /\bhalibut/i, /\bmonkfish/i,
                /\bsea bass/i, /\bturbot/i, /\bsnapper/i,
                /\bmackerel/i, /\bfish\b/i, /\buni\b/i, /\broe\b/i,
                /\bcaviar/i],
  sesame:    [/\bsesame/i, /\btahini/i],
  sulphites: [/\bsulphite/i, /\bsulfite/i, /\bdried fruit/i,
                /\bwine\b/i],
  mustard:   [/\bmustard/i, /\bdijon/i],
  celery:    [/\bcelery/i, /\bcelery seed/i, /\bcelery salt/i],
};


/**
 * Check whether a menu item or recipe conflicts with the diner's
 * saved profile.
 *
 * @param {object} item     — { name, description, tags, dietary_tags, ingredients, allergens }
 * @param {object} profile  — { allergens: [], dietary_tags: [], ailments: [] }
 * @returns {{
 *   hasAllergen: boolean,       // hard block — red overlay
 *   missingDiet: boolean,       // preference miss — greyed
 *   reasons:     string[],      // human strings for tooltips
 *   allergens:   string[],      // codes that triggered the block
 * }}
 */
export function checkConflicts(item, profile) {
  const reasons = [];
  const triggered = [];

  const profileAllergens = (profile?.allergens || []).map((a) =>
    a.toLowerCase());
  const profileDietary   = (profile?.dietary_tags || []).map((d) =>
    d.toLowerCase());

  const itemTags    = (item?.tags || []).map((t) => t.toLowerCase());
  const itemDietary = (item?.dietary_tags || []).map((t) => t.toLowerCase());
  const itemAllergensExplicit = (item?.allergens || []).map((a) =>
    a.toLowerCase());

  // Combine the item's own dietary + tags so we can check both
  // 'nuts'-style allergen flags and 'vegan' positive prefs.
  const itemAllSignals = new Set([...itemTags, ...itemDietary]);

  // 1. EXPLICIT allergens declared on the item by the kitchen
  for (const a of itemAllergensExplicit) {
    if (profileAllergens.includes(a)) {
      triggered.push(a);
      reasons.push(`Contains ${a} (your allergen)`);
    }
  }

  // 2. Dietary-tag → allergen inference (e.g. 'nuts' tag → nuts allergen)
  for (const code of itemAllSignals) {
    const implied = TAG_IMPLIES_ALLERGEN[code] || [];
    for (const a of implied) {
      if (profileAllergens.includes(a) && !triggered.includes(a)) {
        triggered.push(a);
        reasons.push(`Contains ${a} (your allergen)`);
      }
    }
  }

  // 3. Absence-of-tag inference (item not marked gluten_free → may
  //    contain gluten). Skip if the item carries `gluten_free`/`dairy_free`.
  for (const allergen of profileAllergens) {
    const safetyTag = ABSENCE_IMPLIES_ALLERGEN[allergen];
    if (safetyTag && !itemAllSignals.has(safetyTag)) {
      // Last resort — scan ingredient names for hints
      const hints = INGREDIENT_ALLERGEN_HINTS[allergen] || [];
      const haystack = (
        ((item?.ingredients || []).map((i) =>
            typeof i === "string" ? i : (i?.name || ""))
          .join(" ") + " " +
          (item?.description || "")).toLowerCase()
      );
      const hit = hints.some((rx) => rx.test(haystack));
      if (hit && !triggered.includes(allergen)) {
        triggered.push(allergen);
        reasons.push(`May contain ${allergen}`);
      }
    }
  }

  // 4. Pure ingredient-text scan for allergens not implied by tags
  for (const allergen of profileAllergens) {
    if (triggered.includes(allergen)) continue;
    const hints = INGREDIENT_ALLERGEN_HINTS[allergen];
    if (!hints) continue;
    const haystack = (
      ((item?.ingredients || []).map((i) =>
          typeof i === "string" ? i : (i?.name || ""))
        .join(" ") + " " +
        (item?.description || "") + " " +
        (item?.name || "")).toLowerCase()
    );
    if (hints.some((rx) => rx.test(haystack))) {
      triggered.push(allergen);
      reasons.push(`May contain ${allergen}`);
    }
  }

  // 5. Positive dietary preference miss (greyed but orderable)
  const itemTagSet = new Set([...itemTags, ...itemDietary]);
  const missing = profileDietary.filter((d) => !itemTagSet.has(d));
  if (missing.length > 0) {
    reasons.push(`Not ${missing.join(" / ")}`);
  }

  return {
    hasAllergen:  triggered.length > 0,
    missingDiet:  missing.length > 0,
    reasons,
    allergens:    triggered,
  };
}


// Local-storage key for guests (no account). Synced from server
// when the customer signs in.
export const PROFILE_KEY = "hp_dietary_profile_v1";

export function loadGuestProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return { allergens: [], dietary_tags: [], ailments: [] };
    const p = JSON.parse(raw);
    return {
      allergens:    Array.isArray(p.allergens)    ? p.allergens    : [],
      dietary_tags: Array.isArray(p.dietary_tags) ? p.dietary_tags
                  : Array.isArray(p.require)      ? p.require
                  : [],
      ailments:     Array.isArray(p.ailments)     ? p.ailments     : [],
    };
  } catch {
    return { allergens: [], dietary_tags: [], ailments: [] };
  }
}

export function saveGuestProfile(profile) {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch { /* private mode */ }
}
